import { ApiRef, createApiRef } from '@backstage/frontend-plugin-api';
import {
  EnumAccessType,
  type RequestElevatePermissionsRequest,
} from '@internal/backstage-plugin-neo4j-permission-elevation-common';

import { PLUGIN_ID } from './const';

export type ElevationRequest = RequestElevatePermissionsRequest &
  Required<
    Pick<RequestElevatePermissionsRequest, 'accessType' | 'justification'>
  >;

/** ISO timestamps. The expiry is an estimate: the access can end earlier or later. */
export type ElevationResult = {
  requestedAt: string;
  estimatedExpiresAt: string;
};

export interface PermissionElevationApi {
  getAccessTypes(): Promise<EnumAccessType[]>;
  elevate(request: ElevationRequest): Promise<ElevationResult>;
  deelevate(accessType: EnumAccessType): Promise<void>;
}

export const permissionElevationApiRef: ApiRef<PermissionElevationApi> =
  createApiRef<PermissionElevationApi>().with({
    id: 'permission-elevation-api',
    pluginId: PLUGIN_ID,
  });

export const ACCESS_TYPE_LABELS: Record<EnumAccessType, string> = {
  [EnumAccessType.AccessTypeProduction]: 'Production',
  [EnumAccessType.AccessTypeAdmin]: 'Admin',
  [EnumAccessType.AccessTypePlatformAdmin]: 'Aura platform admin',
};

export const ACCESS_TYPE_DESCRIPTIONS: Record<EnumAccessType, string> = {
  [EnumAccessType.AccessTypeProduction]:
    'Lets you run sensitive kubectl commands against production Kubernetes clusters and change AWS, Azure and GCP resources, which are read-only by default. Request it to resolve a customer incident or mitigate a bad deployment.',
  [EnumAccessType.AccessTypePlatformAdmin]:
    'Production access plus platform infrastructure tasks: account and project teardowns, Sysdig and AWS Organizations management. Request it for that platform work. It does not cover Doppler, Auth0, GitHub or Grafana.',
  [EnumAccessType.AccessTypeAdmin]:
    'Full access to all Kubernetes, AWS and GCP resources. Request it to administer the Aura environment, such as reviewing and updating permission sets.',
};

/** Ordered from the least to the most privileged. */
export const ACCESS_TYPES = [
  EnumAccessType.AccessTypeProduction,
  EnumAccessType.AccessTypePlatformAdmin,
  EnumAccessType.AccessTypeAdmin,
];
